'use client';

import * as Sentry from '@sentry/nextjs';
import type { IntlError } from 'use-intl';

/**
 * 已上报的 namespace 组合集合，避免同一页面同一 namespace 重复上报。
 * key 格式: `${pathname}|${namespace}`
 */
const reportedKeys = new Set<string>();

/**
 * 已加载的精确命名空间路径集合。
 * 存储实际的 namespace 路径字符串（如 'account.memberLevel'），
 * 而非仅顶层 key（如 'account'），以实现精确的覆盖检测。
 */
const loadedNamespacePaths = new Set<string>();

/**
 * 注册初始 messages 的 namespace。
 * 应在 I18nPreloadProvider 挂载时调用一次。
 * 优先使用 __loadedNamespaces 元数据，回退到顶层 key 提取。
 */
export function setInitialMessages(messages: Record<string, any>): void {
  if (loadedNamespacePaths.size > 0) return; // 只注册一次
  const paths = extractNamespacePaths(messages);
  paths.forEach((p) => loadedNamespacePaths.add(p));
}

/**
 * 追加路由切换时按需加载的 namespace。
 * 由 RouteMessagesProvider 在合并新 messages 时调用。
 * 优先使用 __loadedNamespaces 元数据实现精确追踪。
 */
export function addAvailableNamespaces(messages: Record<string, any>): void {
  const paths = extractNamespacePaths(messages);
  paths.forEach((p) => loadedNamespacePaths.add(p));
}

/**
 * 从 messages 对象中提取命名空间路径列表。
 * 优先读取 getMessagesForRoute 附加的 __loadedNamespaces 元数据，
 * 回退到顶层 key 提取（兼容旧调用方式）。
 */
function extractNamespacePaths(messages: Record<string, any>): string[] {
  if (messages.__loadedNamespaces && Array.isArray(messages.__loadedNamespaces)) {
    return messages.__loadedNamespaces as string[];
  }
  return Object.keys(messages);
}

/**
 * next-intl onError 回调：捕获缺失的翻译 key 并上报到 Sentry。
 *
 * 仅处理 MISSING_MESSAGE 类型的错误（即 messages 中找不到对应 key），
 * 其他类型（格式化错误、环境回退等）保持 next-intl 默认行为。
 */
export function onI18nError(error: typeof IntlError.prototype): void {
  // 仅关注缺失 key 的场景
  if (error.code !== 'MISSING_MESSAGE') return;

  // 从错误信息中提取 namespace 和 key
  // next-intl 的错误信息格式: "Could not resolve `key` in namespace `namespace`"
  const namespaceMatch = error.message.match(/in namespace `([^`]+)`/);
  const keyMatch = error.message.match(/resolve `([^`]+)`/);
  const namespace = namespaceMatch?.[1];
  const key = keyMatch?.[1];

  if (!namespace || !key) {
    // next-intl 的报错格式变化时，避免把所有事件误分组为 unknown.unknown。
    // 原始错误保存在 extra 中，便于后续更新解析规则。
    reportMissingNamespace('unparsed', undefined, {
      isMissingMessage: true,
      rawMessage: error.message,
    });
    return;
  }

  reportMissingNamespace(namespace, key);
}

/**
 * 检查 useTranslations(namespace) 调用是否超出了已加载的命名空间范围。
 * 使用精确路径覆盖检测：
 * - 精确匹配：namespace 在 loadedNamespacePaths 中
 * - 父路径覆盖：某个已加载路径是 namespace 的前缀
 *   例如加载了 'account' → 'account.myOrders' 被覆盖
 *   但只加载 'account.memberLevel' → 'account.myOrders' 不被覆盖
 */
export function trackNamespaceAccess(namespace: string | undefined): void {
  if (!namespace) return;

  // 精确匹配
  if (loadedNamespacePaths.has(namespace)) return;

  // 父路径覆盖：已加载路径是 namespace 的前缀
  if (
    Array.from(loadedNamespacePaths).some(
      (loaded) => namespace === loaded || namespace.startsWith(loaded + '.'),
    )
  ) {
    return;
  }

  reportMissingNamespace(namespace);
}

/**
 * 统一上报缺失的 namespace。
 */
function reportMissingNamespace(
  namespace: string,
  key?: string,
  options?: { isMissingMessage?: boolean; rawMessage?: string },
): void {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';

  // 去重：同一页面同一 namespace 只上报一次
  const dedupeKey = `${pathname}|${namespace}`;
  if (reportedKeys.has(dedupeKey)) return;
  reportedKeys.add(dedupeKey);

  const message = options?.isMissingMessage
    ? `[i18n] MISSING_MESSAGE: ${namespace}`
    : key
      ? `[i18n] MISSING_MESSAGE: ${namespace}.${key}`
      : `[i18n] NAMESPACE_NOT_IN_ROUTE_CONFIG: ${namespace}`;

  Sentry.captureMessage(message, {
    level: 'warning',
    tags: {
      'i18n.namespace': namespace,
    },
    extra: {
      pathname,
      namespace,
      ...(key ? { key } : {}),
      ...(options?.rawMessage ? { rawMessage: options.rawMessage } : {}),
    },
  });
}
